import React, { useEffect, useState } from "react";
import { Grid, Paper } from "@mui/material";
import type {
  GridColumnVisibilityModel,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowId,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import type { GridFeatureMode } from "@mui/x-data-grid/models/gridFeatureMode";
import type { GridRowsProp } from "@mui/x-data-grid/models/gridRows";
import type { GridSortDirection, GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import type { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import StickyLoadingBar from "./StickyLoadingBar";

interface DataTableProps<T extends GridValidRowModel> {
  columns: GridColDef<T>[];
  rows: GridRowsProp<T>;
  pagingMode?: GridFeatureMode;
  rowCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  loading?: boolean;
  initialState?: GridInitialStateCommunity;
  sortingMode?: GridFeatureMode;
  /** Pass alongside `onSortModelChange` so a sort restored from the URL shows in the header. */
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (visModel: GridColumnVisibilityModel) => void;
  hideFooter?: boolean;
  sortingOrder?: GridSortDirection[] | undefined;
  pageSizeOptions?: number[];
  /** Required when rows have no `id` field — the grid throws rather than falling back. */
  getRowId?: (row: T) => GridRowId;
  /**
   * Row height per row. Rows are one fixed height unless this says otherwise; returning `"auto"`
   * measures the row's own content, which a table of multi-line cells needs.
   */
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
}

export function DataTable<T extends GridValidRowModel>(props: DataTableProps<T>) {
  const {
    rows,
    columns,
    rowCount,
    initialState,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    loading,
    pagingMode,
    sortingMode,
    sortModel,
    onSortModelChange,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    hideFooter,
    sortingOrder = ["asc", "desc"],
    pageSizeOptions = [25, 50, 100],
    getRowId,
    getRowHeight,
  } = props;

  const [rowCountState, setRowCountState] = useState(rowCount || 0);

  useEffect(() => {
    setRowCountState((prev) => (rowCount !== undefined ? rowCount : prev));
  }, [rowCount, setRowCountState]);

  const paginationModel = page === undefined ? undefined : { page, pageSize: pageSize ?? 100 };
  const gridInitialState =
    page === undefined
      ? { ...initialState, pagination: { paginationModel: { pageSize: pageSize ?? 100 } } }
      : initialState;

  return (
    <>
      <StickyLoadingBar loading={loading} ariaLabel="Loading table data" />
      <Grid container>
        <Grid size={12}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              initialState={gridInitialState}
              onPaginationModelChange={(model) => {
                onPageChange?.(model.page);
                onPageSizeChange?.(model.pageSize);
              }}
              pageSizeOptions={pageSizeOptions}
              loading={loading}
              autoHeight
              paginationMode={pagingMode}
              rowCount={pagingMode === "server" ? rowCountState : undefined}
              sortingMode={sortingMode}
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              disableColumnFilter
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={onColumnVisibilityModelChange}
              hideFooter={hideFooter}
              sortingOrder={sortingOrder}
              getRowId={getRowId}
              getRowHeight={getRowHeight}
              // Avoid a second, duplicate-looking bar alongside StickyLoadingBar above.
              slotProps={{ loadingOverlay: { variant: "circular-progress" } }}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
